import React from "react";
import Section from "./Section";

export default function HTML_Introduction() {
  return (
    <div className="tutorial-container">
      {/* Title */}
      <h1 className="tutorial-title">🌐 HTML — Full Introduction</h1>

      {/* Intro */}
      <div
        style={{
          background: "linear-gradient(135deg, #fef3c7, #fde68a)",
          padding: "1rem",
          borderRadius: "12px",
          marginTop: "2rem",
          marginBottom: "2rem",
        }}
      >
        <p className="tutorial-intro">
          Welcome! This is a complete, practical introduction to HTML (
          <strong>HyperText Markup Language</strong>). You’ll learn what HTML
          is, its history, elements, attributes, accessibility, and best
          practices. With examples you can try instantly.
        </p>
      </div>

      {/* Section 1 — What is HTML */}
      <ul className="tutorial-list-points">
        <li>HTML is not a programming language — it’s a markup language.</li>
        <li>Defines elements (tags) that represent content.</li>
        <li>Works with CSS (styling) and JavaScript (functionality).</li>
      </ul>

      <Section
        title="🧠 What is HTML?"
        html={`<!DOCTYPE html>
<html>
  <head>
    <title>What is HTML</title>
  </head>
  <body>
    <h1>Hello HTML!</h1>
    <p>HTML stands for <b>HyperText Markup Language</b>.</p>
    <p>It structures content on web pages like headings, paragraphs, images, and links.</p>
  </body>
</html>`}
        description="HTML is the standard markup language used to create and structure content on the web. It defines the meaning and structure of web elements such as headings, paragraphs, links, images, and lists."
      />

      {/* Section 2 — A Very Short History */}
      <Section
        title="📜 A Very Short History"
        html={`<!DOCTYPE html>
<html>
  <head>
    <title>HTML History</title>
  </head>
  <body>
    <p>HTML was invented by <b>Tim Berners-Lee</b> in 1991.</p>
    <ul>
      <li>1991: HTML 1.0 introduced basic page structure.</li>
      <li>1999: HTML4 standardized layout and forms.</li>
      <li>2014: HTML5 added video, audio, and semantic elements.</li>
    </ul>
  </body>
</html>`}
        description="HTML evolved from simple text pages to today's rich multimedia web with HTML5 introducing powerful semantic and multimedia support."
      />

      {/* Section 3 — Basic Structure of HTML Document */}
      <Section
        title="🏗️ Basic Structure of an HTML Document"
        html={`<!DOCTYPE html>
<html>
  <head>
    <title>HTML Structure</title>
  </head>
  <body>
    <h1>Welcome to My Page!</h1>
    <p>This is a simple HTML structure example.</p>
  </body>
</html>`}
        description="Every HTML document follows a basic structure with the <html>, <head>, and <body> tags. The <head> holds metadata and the <title>, while the <body> contains the visible content of the page."
      />

      <ul className="tutorial-list-points">
        <li>
          <strong>&lt;html&gt;</strong> — Root element wrapping all page
          content.
        </li>
        <li>
          <strong>&lt;head&gt;</strong> — Contains metadata, links, and the
          &lt;title&gt; tag.
        </li>
        <li>
          <strong>&lt;title&gt;</strong> — Defines the title shown in browser
          tabs.
        </li>
        <li>
          <strong>&lt;body&gt;</strong> — Contains all visible content like
          text, images, and links.
        </li>
        <li>
          <strong>&lt;/body&gt; &amp; &lt;/html&gt;</strong> — Closing tags
          marking the end of sections.
        </li>
      </ul>

      {/* Section 4 — Elements, Tags, and Attributes */}
      <Section
        title="2️⃣ Elements, Tags, and Attributes"
        html={`<!DOCTYPE html>
<html>
  <head>
    <title>HTML Elements</title>
  </head>
  <body>
    <p>An element usually has an opening and closing tag, e.g.:</p>
    <code>&lt;p&gt;This is a paragraph.&lt;/p&gt;</code>
    <p>Attributes add extra information to elements:</p>
    <code>&lt;img src="photo.jpg" alt="Description" /&gt;</code>
  </body>
</html>`}
        description="Elements are building blocks of HTML. Tags define their start and end, while attributes add more details like image sources or links."
      />

      <ul className="tutorial-list-points">
        <li>
          <strong>Element:</strong> <code>&lt;p&gt;Hi&lt;/p&gt;</code>
        </li>
        <li>
          <strong>Tag:</strong> The opening and closing parts like{" "}
          <code>&lt;p&gt;</code> and <code>&lt;/p&gt;</code>
        </li>
        <li>
          <strong>Attribute:</strong> e.g.{" "}
          <code>&lt;img src="photo.jpg" alt="..." /&gt;</code>
        </li>
      </ul>

      {/* Section 5 — Accessibility */}
      <Section
        title="♿ Accessibility Basics (a11y)"
        html={`<!DOCTYPE html>
<html>
  <head>
    <title>Accessibility Basics</title>
  </head>
  <body>
    <img src="https://via.placeholder.com/120" alt="Sample image for accessibility" />
    <h3>Accessible HTML Example</h3>
    <label for="name">Name:</label>
    <input id="name" type="text" />
  </body>
</html>`}
        description="Accessibility ensures everyone, including users with disabilities, can use your website effectively."
      />

      <ul className="tutorial-list-points">
        <li>Always use alt text for images.</li>
        <li>Follow correct heading order (h1 → h2 → h3).</li>
        <li>Label all form controls properly.</li>
        <li>Use ARIA attributes only when necessary.</li>
        <li>Ensure keyboard navigation and good color contrast.</li>
      </ul>

      {/* Section 6 — SEO Tips */}
      <Section
        title="6️⃣ SEO — Quick Tips"
        html={`<!DOCTYPE html>
<html>
  <head>
    <title>SEO Tips</title>
    <meta name="description" content="Learn HTML from basics to advanced!" />
  </head>
  <body>
    <h1>Best HTML Tutorial</h1>
  </body>
</html>`}
        description="SEO (Search Engine Optimization) helps search engines understand your site’s structure and content."
      />

      <ul className="tutorial-list-points">
        <li>Use descriptive title and meta description.</li>
        <li>Follow semantic HTML structure.</li>
        <li>Use meaningful alt text for images.</li>
        <li>Keep URLs short and human-readable.</li>
      </ul>

      {/* Section 7 — Best Practices */}
      <Section
        title="7️⃣ Best Practices"
        html={`<!DOCTYPE html>
<html>
  <head>
    <title>HTML Best Practices</title>
  </head>
  <body>
    <header>
      <h1>Welcome!</h1>
    </header>
    <main>
      <p>Keep structure, style, and behavior separated.</p>
    </main>
  </body>
</html>`}
        description="Following best practices ensures maintainability and accessibility of your HTML code."
      />

      <ul className="tutorial-list-points">
        <li>Always close tags properly.</li>
        <li>Separate structure (HTML) from style (CSS).</li>
        <li>Use progressive enhancement.</li>
        <li>Optimize assets and images.</li>
        <li>Design mobile-first.</li>
      </ul>

      {/* Section 8 — Quick Cheat Sheet */}
      <div
        className="tutorial-section"
        style={{
          background: "linear-gradient(135deg, #d1fae5, #a7f3d0)",
          padding: "1rem",
          borderRadius: "12px",
          marginTop: "2rem",
        }}
      >
        <h3>9️⃣ Quick Cheat-Sheet</h3>
        <ul className="tutorial-list-points">
          <li>Structural: html, head, body, header, main, footer, nav</li>
          <li>Text: h1–h6, p, strong, em, blockquote, code</li>
          <li>Lists: ul, ol, li</li>
          <li>Links/images: a, img, figure, figcaption</li>
          <li>Media: video, audio, canvas</li>
          <li>Forms: form, input, textarea, select, button</li>
          <li>Table: table, thead, tbody, tr, td, th</li>
        </ul>
      </div>

      {/* Summary */}
      <div
        style={{
          background: "linear-gradient(135deg, #fef3c7, #fde68a)",
          padding: "1rem",
          borderRadius: "12px",
          marginTop: "2rem",
        }}
      >
        <h2>📘 Summary</h2>
        <ul className="tutorial-list-points">
          <li>HTML structures web pages using elements and tags.</li>
          <li>Attributes add extra information to elements.</li>
          <li>Follow accessibility and SEO principles.</li>
          <li>Always use semantic HTML where possible.</li>
          <li>Combine HTML with CSS and JavaScript for complete web apps.</li>
        </ul>
      </div>
    </div>
  );
}
